import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { experimentalStyled as styling } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import secureLocalStorage from "react-secure-storage";
import { getDownloadURL, ref } from "firebase/storage";

import { imageDb } from "../Firebase/Config";
import ImageUpload from "../pages/ImageUpload/UploadImage";

const BootstrapDialog = styling(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 24,
    width: "100%",
    maxWidth: 440,
    overflow: "hidden",
    boxShadow: "0 24px 70px rgba(15, 23, 42, .24)",
  },
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2.5),
  },
}));

function getStoredUser() {
  try {
    return JSON.parse(secureLocalStorage.getItem("profile"));
  } catch {
    return null;
  }
}

function getInitials(firstName, lastName) {
  const first = firstName?.[0] || "";
  const last = lastName?.[0] || "";

  return `${first}${last}`.toUpperCase() || "U";
}

export default function ProfileImage() {
  const user = React.useMemo(() => getStoredUser(), []);

  const firstName = user?.results?.firstName || "";
  const lastName = user?.results?.lastName || "";
  const email = user?.results?.email || "";
  const branch = user?.results?.branch || "";
  const role = user?.results?.userRole || "";

  const initials = getInitials(firstName, lastName);
  const fullName = `${firstName} ${lastName}`.trim() || "Admin User";

  const [openDialog, setOpenDialog] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(null);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDialogClickOpen = () => {
    setOpenDialog(true);
  };

  React.useEffect(() => {
    let active = true;

    const fetchProfileImage = async () => {
      try {
        if (!email) return;

        const fileName = `${email}`;
        const storageRef = ref(imageDb, `profile_images/${fileName}`);
        const downloadURL = await getDownloadURL(storageRef);

        if (active) {
          setImageUrl(downloadURL);
        }
      } catch (error) {
        if (active) {
          setImageUrl(null);
        }

        console.error("Error fetching the image:", error);
      }
    };

    fetchProfileImage();

    return () => {
      active = false;
    };
  }, [email]);

  return (
    <>
      <BootstrapDialog
        onClose={handleDialogClose}
        aria-labelledby="profile-image-dialog-title"
        open={openDialog}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#fff",
            px: 2.5,
            py: 2,
            position: "relative",
          }}
        >
          <DialogTitle
            id="profile-image-dialog-title"
            sx={{
              m: 0,
              p: 0,
              fontWeight: 900,
              fontSize: 18,
              color: "#fff",
            }}
          >
            Admin Account
          </DialogTitle>

          <Typography
            sx={{
              color: "#cbd5e1",
              fontSize: 12,
              mt: 0.5,
            }}
          >
            Manage your profile picture
          </Typography>

          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#cbd5e1",
              background: "rgba(255,255,255,.08)",
              "&:hover": {
                background: "rgba(255,255,255,.16)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            background:
              "linear-gradient(135deg, #eef4ff 0%, #f8fafc 48%, #eefdf5 100%)",
          }}
        >
          <Stack spacing={2.2} alignItems="center">
            <Box
              sx={{
                width: "100%",
                p: 2,
                borderRadius: 4,
                background: "rgba(255,255,255,.88)",
                border: "1px solid #e5e7eb",
                boxShadow: "0 14px 35px rgba(15, 23, 42, .08)",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    p: "3px",
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #1677ff 0%, #22c55e 100%)",
                  }}
                >
                  <Avatar
                    alt={initials}
                    src={imageUrl || undefined}
                    sx={{
                      width: 74,
                      height: 74,
                      fontWeight: 900,
                      fontSize: 24,
                      color: "#fff",
                      background:
                        "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
                      border: "4px solid #fff",
                    }}
                  >
                    {initials}
                  </Avatar>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: "#0f172a",
                      fontSize: 16,
                      lineHeight: 1.2,
                    }}
                  >
                    {fullName}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: 12,
                      wordBreak: "break-all",
                      mt: 0.4,
                    }}
                  >
                    {email || "No email available"}
                  </Typography>

                  <Stack direction="row" spacing={0.7} flexWrap="wrap" mt={1}>
                    {role && (
                      <Chip
                        size="small"
                        icon={<VerifiedUserRoundedIcon />}
                        label={role}
                        sx={{
                          fontWeight: 800,
                          borderRadius: 999,
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          "& .MuiChip-icon": {
                            color: "#1d4ed8",
                          },
                        }}
                      />
                    )}

                    {branch && (
                      <Chip
                        size="small"
                        label={branch}
                        sx={{
                          fontWeight: 800,
                          borderRadius: 999,
                          background: "#f0fdf4",
                          color: "#15803d",
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                width: "100%",
                p: 2,
                borderRadius: 4,
                background: "#ffffff",
                border: "1px dashed #bfdbfe",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 3,
                    background: "#eff6ff",
                    color: "#1d4ed8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PhotoCameraRoundedIcon fontSize="small" />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: "#0f172a",
                      fontSize: 14,
                    }}
                  >
                    Profile Picture
                  </Typography>

                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: 12,
                      mt: 0.3,
                    }}
                  >
                    A profile picture helps people recognize you and confirms
                    when you are logged in to your account.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                width: "100%",
                p: 2,
                borderRadius: 4,
                background: "#fff",
                border: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "center",
                boxShadow: "0 12px 30px rgba(15, 23, 42, .06)",
              }}
            >
              <ImageUpload />
            </Box>
          </Stack>
        </DialogContent>
      </BootstrapDialog>

      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          p: "3px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #1677ff 0%, #22c55e 100%)",
          boxShadow: "0 14px 28px rgba(22, 119, 255, .24)",
        }}
      >
        <Avatar
          alt={initials}
          src={imageUrl || undefined}
          sx={{
            width: 52,
            height: 52,
            fontWeight: 900,
            fontSize: 18,
            color: "#fff",
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            border: "3px solid #fff",
          }}
        >
          {initials}
        </Avatar>

        <Tooltip title="Edit profile image">
          <IconButton
            onClick={handleDialogClickOpen}
            size="small"
            sx={{
              position: "absolute",
              right: -4,
              bottom: -2,
              width: 25,
              height: 25,
              background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
              color: "#fff",
              border: "2px solid #fff",
              boxShadow: "0 8px 18px rgba(15, 23, 42, .28)",
              "&:hover": {
                background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
              },
            }}
          >
            <EditRoundedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
}
